import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import {GRAY_DARKER} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Modal from 'ui-library/modals/Modal';

/**
 * Formats the validated cloud version based on the SHOW_VALIDATED_CLOUD_PATCH_VERSION flag.
 * If the flag is true, returns the full version. If false, returns only major.minor version.
 */
function formatValidatedCloudVersion(version: string): string {
  if (BenchlingConfig.SHOW_VALIDATED_CLOUD_PATCH_VERSION) {
    return version;
  }

  // Extract only major.minor version (e.g., "2025.3" from "2025.3.4")
  const versionParts = version.split('.');
  if (versionParts.length >= 2) {
    return `${versionParts[0]}.${versionParts[1]}`;
  }

  // If version doesn't have expected format, return as-is
  return version;
}

interface DetailProps {
  name: string;
  value: string;
}

function Detail(props: DetailProps): JSX.Element {
  return (
    <div className={css(styles.detail)}>
      <strong>{props.name}:</strong> <span className={css(styles.valueText)}>{props.value}</span>
    </div>
  );
}

export interface Props {
  onRequestClose: () => void;
}

/**
 * A modal that shows the product version; this is a Validated Cloud requirement.
 */
export default function AboutBenchlingModal(props: Props): JSX.Element {
  return (
    <Modal onRequestClose={props.onRequestClose} suppressHeader={true} wrapInForm={false} small={true}>
      <div className="u-small-spacing-bottom u-lr-split">
        <img className={css(styles.wordmark)} src="/static/img/gradient_wordmark_500.png" />
        <BenchlingIcon className="close" icon="close" data-dismiss="modal" />
      </div>
      <Detail name="Version" value={formatValidatedCloudVersion(BenchlingConfig.VALIDATED_CLOUD_VERSION)} />
      <Detail name="Product" value="Validated Cloud" />
      <Detail name="Tenant ID" value={BenchlingConfig.TENANT_NAME} />
    </Modal>
  );
}

/**
 * For now, just show the modal if we have a Validated Cloud version. In the future, we may also show the
 * Continuous Release version, beneficial for customers on both validated and non-validated environments.
 */
export function isAboutBenchlingModalEnabled(): boolean {
  return Boolean(BenchlingConfig.VALIDATED_CLOUD_VERSION);
}

const styles = StyleSheet.create({
  detail: {
    ...FONT_PROXIMA_13,
    marginBottom: 10,
  },
  wordmark: {
    width: 140,
  },
  valueText: {
    color: GRAY_DARKER,
  },
});
